import React from "react";
import {
  Home as HomeIcon, Wrench, Droplets, Flame, Sun, Zap, DoorOpen, Thermometer,
  Bath, Truck, Bug, PaintBucket, Square, Wind, Settings, Trash2,
} from "lucide-react";

/**
 * OffersGalaxy
 * ------------
 * Space-themed hero visual: a starry galaxy backdrop with home-service
 * offers floating around like planets. Multi-layer:
 *   - Deep starfield (small twinkling dots)
 *   - Nebula clouds (radial gradients)
 *   - Slow spiral rotation
 *   - Offer "planets" positioned on 3 orbit rings, drifting
 *   - Central bright core
 */

// Each offer sits on one of 3 orbit rings (inner / middle / outer).
// Ring, angle in degrees, icon, label, payout, tint.
const OFFERS = [
  // Inner ring (closer to core)
  { ring: 1, angle:  20, icon: Thermometer,  label: "HVAC",         payout: "$94",  tint: "from-cyan-500 to-blue-500" },
  { ring: 1, angle: 140, icon: Wrench,       label: "Plumbers",     payout: "$98",  tint: "from-blue-600 to-blue-400" },
  { ring: 1, angle: 260, icon: Zap,          label: "Electricians", payout: "$57",  tint: "from-yellow-500 to-amber-500" },
  // Middle ring
  { ring: 2, angle:   0, icon: HomeIcon,     label: "Roofers",      payout: "$178", tint: "from-blue-500 to-cyan-500" },
  { ring: 2, angle:  72, icon: Droplets,     label: "Water & Fire", payout: "$515", tint: "from-red-500 to-orange-500" },
  { ring: 2, angle: 144, icon: Flame,        label: "Heating",      payout: "$96",  tint: "from-orange-500 to-yellow-500" },
  { ring: 2, angle: 216, icon: Sun,          label: "Solar",        payout: "$53",  tint: "from-yellow-400 to-orange-400" },
  { ring: 2, angle: 288, icon: DoorOpen,     label: "Garage",       payout: "$77",  tint: "from-slate-500 to-slate-400" },
  // Outer ring
  { ring: 3, angle:  40, icon: Bath,         label: "Bathroom",     payout: "$54",  tint: "from-purple-500 to-pink-500" },
  { ring: 3, angle: 100, icon: Truck,        label: "Movers",       payout: "$23",  tint: "from-indigo-500 to-purple-500" },
  { ring: 3, angle: 160, icon: Bug,          label: "Pest Ctrl",    payout: "$56",  tint: "from-green-600 to-green-400" },
  { ring: 3, angle: 220, icon: PaintBucket,  label: "Painters",     payout: "$34",  tint: "from-pink-500 to-rose-500" },
  { ring: 3, angle: 280, icon: Square,       label: "Windows",      payout: "$56",  tint: "from-sky-500 to-blue-500" },
  { ring: 3, angle: 340, icon: Wind,         label: "Gutters",      payout: "$30",  tint: "from-teal-500 to-cyan-500" },
];

// Random-ish starfield positions (fixed so it doesn't shift on re-render).
const STARS = Array.from({ length: 60 }).map((_, i) => ({
  id: i,
  top: Math.random() * 100,
  left: Math.random() * 100,
  size: Math.random() < 0.85 ? 1 : 2,
  delay: Math.random() * 3,
  dur: 2 + Math.random() * 3,
}));

const OffersGalaxy = () => (
  <div
    className="offers-galaxy relative aspect-square w-full max-w-[520px] mx-auto"
    aria-hidden="true"
    data-testid="offers-galaxy"
  >
    {/* Deep space background */}
    <div className="galaxy-bg" />

    {/* Nebula clouds */}
    <div className="nebula nebula-1" />
    <div className="nebula nebula-2" />
    <div className="nebula nebula-3" />

    {/* Starfield */}
    {STARS.map((s) => (
      <span
        key={s.id}
        className="galaxy-star"
        style={{
          top: `${s.top}%`,
          left: `${s.left}%`,
          width: `${s.size}px`,
          height: `${s.size}px`,
          animationDelay: `${s.delay}s`,
          animationDuration: `${s.dur}s`,
        }}
      />
    ))}

    {/* Slow rotating spiral arms — very subtle */}
    <div className="galaxy-spiral" />

    {/* Central bright core */}
    <div className="galaxy-core">
      <span className="galaxy-core-eyebrow">HOME SERVICES · USA</span>
      <span className="galaxy-core-title">Pay Per Call Network</span>
    </div>

    {/* Orbit rings (visual only) */}
    <div className="orbit-ring orbit-ring-1" />
    <div className="orbit-ring orbit-ring-2" />
    <div className="orbit-ring orbit-ring-3" />

    {/* Offer planets — 3 orbit shells, each rotating independently */}
    {[1, 2, 3].map((ring) => (
      <div key={ring} className={`offer-orbit offer-orbit-${ring}`}>
        {OFFERS.filter((o) => o.ring === ring).map((o) => {
          const rad = (o.angle * Math.PI) / 180;
          const rPct = ring === 1 ? 22 : ring === 2 ? 33 : 43; // orbit radius %
          const top = 50 - Math.cos(rad) * rPct;
          const left = 50 + Math.sin(rad) * rPct;
          return (
            <div
              key={o.label}
              className="offer-planet"
              style={{ top: `${top}%`, left: `${left}%` }}
            >
              <div className={`offer-planet-inner bg-gradient-to-br ${o.tint}`}>
                <o.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.25} />
              </div>
              <div className="offer-planet-label">
                <span className="offer-planet-name">{o.label}</span>
                <span className="offer-planet-payout">{o.payout}</span>
              </div>
            </div>
          );
        })}
      </div>
    ))}

    <style>{`
      /* Backdrop — natural, no visible circle. Soft radial fade so the
         galaxy blends into whatever hero background sits behind it. */
      .offers-galaxy {
        background: transparent;
        /* Fade content out at edges for a natural, borderless galaxy look */
        -webkit-mask-image: radial-gradient(ellipse at 50% 50%,
          black 55%, transparent 90%);
        mask-image: radial-gradient(ellipse at 50% 50%,
          black 55%, transparent 90%);
      }
      .galaxy-bg {
        position: absolute; inset: 0;
        background:
          radial-gradient(ellipse at 50% 50%,
            rgba(30, 27, 75, 0.85) 0%,
            rgba(15, 23, 42, 0.7) 40%,
            transparent 75%),
          radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.28), transparent 40%),
          radial-gradient(circle at 70% 80%, rgba(147, 51, 234, 0.22), transparent 45%);
      }

      /* Nebula soft clouds ----------------------------------------- */
      .nebula {
        position: absolute;
        border-radius: 9999px;
        filter: blur(30px);
        opacity: 0.55;
        pointer-events: none;
        animation: nebula-drift 30s ease-in-out infinite;
      }
      .nebula-1 {
        top: 10%; left: -8%;
        width: 55%; aspect-ratio: 1;
        background: radial-gradient(circle, rgba(59,130,246,0.55), transparent 65%);
      }
      .nebula-2 {
        bottom: 8%; right: -6%;
        width: 60%; aspect-ratio: 1;
        background: radial-gradient(circle, rgba(147,51,234,0.5), transparent 60%);
        animation-delay: -10s;
      }
      .nebula-3 {
        top: 50%; left: 40%;
        width: 40%; aspect-ratio: 1;
        transform: translate(-50%, -50%);
        background: radial-gradient(circle, rgba(34,211,238,0.4), transparent 65%);
        animation-delay: -20s;
      }
      @keyframes nebula-drift {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50%      { transform: translate(3%, -2%) scale(1.08); }
      }
      .nebula-3 { animation-name: nebula-drift-c; }
      @keyframes nebula-drift-c {
        0%, 100% { transform: translate(-50%, -50%) scale(1); }
        50%      { transform: translate(-48%, -52%) scale(1.12); }
      }

      /* Starfield ------------------------------------------------- */
      .galaxy-star {
        position: absolute;
        background: white;
        border-radius: 9999px;
        opacity: 0.85;
        box-shadow: 0 0 3px rgba(255,255,255,0.9);
        animation: star-twinkle 3s ease-in-out infinite;
      }
      @keyframes star-twinkle {
        0%, 100% { opacity: 0.85; transform: scale(1); }
        50%      { opacity: 0.35; transform: scale(0.6); }
      }

      /* Slow spiral overlay ------------------------------------- */
      .galaxy-spiral {
        position: absolute;
        inset: -10%;
        border-radius: 9999px;
        background:
          conic-gradient(from 0deg,
            transparent 0deg,
            rgba(147, 197, 253, 0.06) 60deg,
            transparent 120deg,
            rgba(196, 181, 253, 0.06) 180deg,
            transparent 240deg,
            rgba(147, 197, 253, 0.06) 300deg,
            transparent 360deg);
        animation: spiral-spin 90s linear infinite;
        pointer-events: none;
        filter: blur(6px);
      }
      @keyframes spiral-spin { to { transform: rotate(360deg); } }

      /* Central core ------------------------------------------- */
      .galaxy-core {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        z-index: 5;
        padding: 12px 18px;
        border-radius: 9999px;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(148, 163, 184, 0.25);
        box-shadow:
          0 0 30px rgba(59, 130, 246, 0.35),
          0 0 60px rgba(147, 51, 234, 0.2);
      }
      .galaxy-core-eyebrow {
        font-size: 9px;
        letter-spacing: 0.22em;
        font-weight: 700;
        text-transform: uppercase;
        color: #a5f3fc;
        margin-bottom: 3px;
      }
      .galaxy-core-title {
        font-family: 'Poppins', Inter, sans-serif;
        font-weight: 800;
        font-size: clamp(18px, 3.5vw, 26px);
        color: white;
        line-height: 1;
        letter-spacing: -0.01em;
        white-space: nowrap;
      }

      /* Orbit rings (visual) ---------------------------------- */
      .orbit-ring {
        position: absolute;
        top: 50%;
        left: 50%;
        border-radius: 9999px;
        border: 1px dashed rgba(148, 163, 184, 0.18);
        transform: translate(-50%, -50%);
      }
      .orbit-ring-1 { width: 44%; aspect-ratio: 1; }
      .orbit-ring-2 { width: 66%; aspect-ratio: 1; border-style: dotted; border-color: rgba(56, 189, 248, 0.18); }
      .orbit-ring-3 { width: 86%; aspect-ratio: 1; border-color: rgba(196, 181, 253, 0.15); }

      /* Offer planets ----------------------------------------- */
      .offer-orbit {
        position: absolute; inset: 0;
        pointer-events: none;
      }
      /* Each ring rotates at different speed. Because we positioned each
         planet in JS at a fixed angle (via top/left percentages), rotating
         the ring container also rotates the planet positions. */
      .offer-orbit-1 { animation: orbit-cw  40s linear infinite; }
      .offer-orbit-2 { animation: orbit-ccw 65s linear infinite; }
      .offer-orbit-3 { animation: orbit-cw  95s linear infinite; }
      @keyframes orbit-cw  { to { transform: rotate(360deg); } }
      @keyframes orbit-ccw { to { transform: rotate(-360deg); } }

      .offer-planet {
        position: absolute;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        /* Counter-rotate at the same speed as the ring so labels stay upright */
        animation: orbit-ccw 40s linear infinite;
      }
      .offer-orbit-2 .offer-planet { animation: orbit-cw  65s linear infinite; }
      .offer-orbit-3 .offer-planet { animation: orbit-ccw 95s linear infinite; }

      /* Planet sphere — 3D-looking ball with icon in the center */
      .offer-planet-inner {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px; height: 42px;
        border-radius: 9999px;
        flex-shrink: 0;
        position: relative;
        box-shadow:
          inset -5px -6px 12px rgba(0,0,0,0.45),
          inset 4px 5px 10px rgba(255,255,255,0.28),
          0 6px 14px rgba(0,0,0,0.55),
          0 0 20px rgba(59, 130, 246, 0.35);
      }
      /* Soft top-left highlight to sell the sphere illusion */
      .offer-planet-inner::before {
        content: "";
        position: absolute;
        top: 12%;
        left: 15%;
        width: 40%;
        height: 30%;
        border-radius: 9999px;
        background: radial-gradient(ellipse at 30% 30%,
          rgba(255,255,255,0.55), transparent 65%);
        pointer-events: none;
      }
      .offer-planet-label {
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 1;
        text-align: center;
        gap: 2px;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
      }
      .offer-planet-name {
        font-size: 10px;
        font-weight: 700;
        color: white;
        letter-spacing: 0.02em;
      }
      .offer-planet-payout {
        font-size: 9px;
        font-weight: 700;
        color: #7dd3fc;
      }

      /* Accessibility */
      @media (prefers-reduced-motion: reduce) {
        .nebula, .galaxy-star, .galaxy-spiral,
        .offer-orbit-1, .offer-orbit-2, .offer-orbit-3,
        .offer-planet { animation: none !important; }
      }

      /* Mobile fine-tuning ----------------------------------- */
      @media (max-width: 640px) {
        .offer-planet { gap: 3px; }
        .offer-planet-inner { width: 36px; height: 36px; }
        .offer-planet-name { font-size: 9px; }
        .offer-planet-payout { font-size: 8px; }
      }
    `}</style>
  </div>
);

export default OffersGalaxy;
